import axios from 'axios';
import api from './api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';

export interface FeaturedProduct {
    id: number;
    product_id: number;
    display_order: number;
    is_active: boolean;
    created_at: string;
    code: string;
    designation: string;
    description?: string;
    unit_price: number;
    photo_url?: string;
    is_available: boolean;
    category_name?: string;
}

export interface FeaturedProductsResponse {
    success: boolean;
    featuredProducts: FeaturedProduct[];
}

export interface PublicFeaturedProductsResponse {
    success: boolean;
    products: any[]; // Using same structure as regular products
}

export interface FeaturedProductRequest {
    productId: number;
    displayOrder?: number;
}

export interface UpdateFeaturedProductRequest {
    displayOrder?: number;
    isActive?: boolean;
}

export interface ReorderRequest {
    featuredProducts: { id: number }[];
}

export const featuredProductService = {
    // Get featured products for admin management
    getFeaturedProducts: async (): Promise<FeaturedProductsResponse> => {
        try {
            const response = await api.get('/featured-products');
            return response.data;
        } catch (error: any) {
            console.error('Erreur lors de la récupération des produits en vedette:', error);
            throw new Error(error.response?.data?.error || 'Erreur lors de la récupération des produits en vedette');
        }
    },

    // Get featured products for public display (homepage)
    getFeaturedProductsPublic: async (limit?: number): Promise<PublicFeaturedProductsResponse> => {
        try {
            const params = limit ? { limit } : {};
            const response = await axios.get(`${API_BASE_URL}/featured-products/public`, { params });
            return response.data;
        } catch (error: any) {
            console.error('Erreur lors de la récupération des produits en vedette publics:', error);
            throw new Error(error.response?.data?.error || 'Erreur lors de la récupération des produits en vedette');
        }
    },

    // Add product to featured products
    addFeaturedProduct: async (data: FeaturedProductRequest): Promise<{ success: boolean; message: string; featuredProductId: number }> => {
        try {
            const response = await api.post('/featured-products', data);
            return response.data;
        } catch (error: any) {
            console.error('Erreur lors de l\'ajout du produit en vedette:', error);
            throw new Error(error.response?.data?.error || 'Erreur lors de l\'ajout du produit en vedette');
        }
    },

    // Update featured product
    updateFeaturedProduct: async (id: number, data: UpdateFeaturedProductRequest): Promise<{ success: boolean; message: string }> => {
        try {
            const response = await api.put(`/featured-products/${id}`, data);
            return response.data;
        } catch (error: any) {
            console.error('Erreur lors de la mise à jour du produit en vedette:', error);
            throw new Error(error.response?.data?.error || 'Erreur lors de la mise à jour du produit en vedette');
        }
    },

    // Remove product from featured products
    removeFeaturedProduct: async (id: number): Promise<{ success: boolean; message: string }> => {
        try {
            const response = await api.delete(`/featured-products/${id}`);
            return response.data;
        } catch (error: any) {
            console.error('Erreur lors de la suppression du produit en vedette:', error);
            throw new Error(error.response?.data?.error || 'Erreur lors de la suppression du produit en vedette');
        }
    },

    // Reorder featured products
    reorderFeaturedProducts: async (data: ReorderRequest): Promise<{ success: boolean; message: string }> => {
        try {
            const response = await api.put('/featured-products/reorder/batch', data);
            return response.data;
        } catch (error: any) {
            console.error('Erreur lors de la réorganisation des produits en vedette:', error);
            throw new Error(error.response?.data?.error || 'Erreur lors de la réorganisation des produits en vedette');
        }
    }
};
